/**
 * Logger Utility
 *
 * @module utils/logger
 * @description Centralized logging utility with environment-aware behavior.
 * Provides structured logging with timestamps and log levels. Automatically
 * filters debug logs in production and respects VITE_DEBUG flag.
 *
 * Features:
 * - Timestamped log entries
 * - Environment-aware filtering (debug only in dev)
 * - Consistent formatting across all log levels
 * - Type-safe log level definitions
 *
 * @example
 * ```typescript
 * import logger from '@/utils/logger';
 *
 * logger.debug('Detailed debug info', { userId: 123 });
 * logger.info('User logged in', { email: 'user@example.com' });
 * logger.warn('Token expires soon', { expiresAt: timestamp });
 * logger.error('API call failed', error);
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Logger class for structured logging
 * @class Logger
 */
class Logger {
  private isDevelopment = import.meta.env.DEV;
  private isDebugEnabled = import.meta.env.VITE_DEBUG === 'true';

  /**
   * Internal log method with timestamp and level formatting
   * @private
   */
  private log(level: LogLevel, message: string, ...args: unknown[]): void {
    if (!this.isDevelopment && level === 'debug') {
      return; // Skip debug logs in production
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case 'debug':
        if (this.isDebugEnabled) {
          console.debug(prefix, message, ...args);
        }
        break;
      case 'info':
        console.info(prefix, message, ...args);
        break;
      case 'warn':
        console.warn(prefix, message, ...args);
        break;
      case 'error':
        console.error(prefix, message, ...args);
        break;
    }
  }

  /**
   * Log debug message (only visible in development with VITE_DEBUG=true)
   * @param {string} message - The debug message
   * @param {...unknown[]} args - Additional data to log
   */
  debug(message: string, ...args: unknown[]): void {
    this.log('debug', message, ...args);
  }

  /**
   * Log informational message
   * @param {string} message - The info message
   * @param {...unknown[]} args - Additional data to log
   */
  info(message: string, ...args: unknown[]): void {
    this.log('info', message, ...args);
  }

  /**
   * Log warning message
   * @param {string} message - The warning message
   * @param {...unknown[]} args - Additional data to log
   */
  warn(message: string, ...args: unknown[]): void {
    this.log('warn', message, ...args);
  }

  /**
   * Log error message with optional error object
   * @param {string} message - The error message
   * @param {unknown} [error] - Optional error object or additional data
   */
  error(message: string, error?: unknown): void {
    if (error) {
      this.log('error', message, error);
    } else {
      this.log('error', message);
    }
  }
}

export const logger = new Logger();
export default logger;
